import { createContext, useContext } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Check, RotateCcw } from "react-feather";
import dayjs from "dayjs";
import styles from "./paymentlist.module.css";

/*
 * Refunds are record-only: PropertyDek never sends money back. The manager refunds the
 * tenant outside the app and records it here. The payment row's amount is the net
 * (paid − refunded); original_amount and refunded_amount come from the backend.
 */

// Provided by PaymentList: { openRefund(item, refund?), deleteRefund(item, refund) }
export const RefundContext = createContext(null);

const REFUNDABLE_TYPES = ["online-payment", "record-payment", "add-credit"];

const money = (value) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value || 0);

const round2 = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

export const getRefundInfo = (item) => {
    const refunded = parseFloat(item?.refunded_amount) || 0;
    const original = parseFloat(item?.original_amount ?? item?.amount) || 0;
    const net = parseFloat(item?.amount) || 0;
    const remaining = round2(original - refunded);
    // No money received (failed / pending) means nothing to refund
    const eligible = item?.type === 2 && item?.payment_status === 1 && REFUNDABLE_TYPES.indexOf(item?.payment_type) > -1;

    return {
        eligible,
        original,
        refunded,
        net,
        remaining,
        isFull: refunded > 0 && remaining <= 0,
        isPartial: refunded > 0 && remaining > 0,
    };
};

export const RefundChip = ({ item }) => {
    const info = getRefundInfo(item);
    if (info.isFull) return <span className={`${styles.RefundChip} ${styles.RefundChipFull}`}><RotateCcw /> Refunded</span>;
    if (info.isPartial) return <span className={styles.RefundChip}><RotateCcw /> Partially refunded</span>;
    return null;
};

// The "Paid (Payment)" cell of a received payment or credit
export const RefundPaidAmount = ({ item }) => {
    const info = getRefundInfo(item);

    if (info.isFull) {
        return (<div className={styles.PaymentRefunded}>
            <div className={styles.Label}><RotateCcw /> Refunded</div>
            <div className={`${styles.Amount} ${styles.Struck}`}>{money(info.original)}</div>
            <div className={styles.AmountSub}>Net $0.00</div>
        </div>);
    }

    return (<div className={styles.PaymentOk}>
        <div className={styles.Label}><Check /> Applied</div>
        <div className={styles.Amount}>{money(info.net)}</div>
        {info.isPartial && <div className={styles.AmountSub}>Paid {money(info.original)} · <span className={styles.RefundText}>refunded {money(info.refunded)}</span></div>}
    </div>);
};

const canUpdate = (isAuthenticated, user) => isAuthenticated && user["https://propertydek.com/permissions"].indexOf("income:update") > -1;

// The "Refund details" block in an expanded row, one section per recorded refund
export const RefundDetails = ({ item }) => {
    const { user, isAuthenticated } = useAuth0();
    const refundCtx = useContext(RefundContext);
    const info = getRefundInfo(item);
    const refunds = item?.refunds || [];

    if (!refunds.length) return null;

    return (<div className={styles.RefundDetailsSec}>
        <div className={styles.RefundDetailsHead}>
            <span className={styles.RefundDetailsTitle}><RotateCcw /> Refund details</span>
            <span className={styles.RefundDetailsSummary}>{info.isFull ? "Full refund" : "Partial refund"} · {money(info.refunded)} of {money(info.original)}</span>
        </div>
        {refunds.map((refund) => (<div key={refund.id} className={styles.RefundDetailsItem}>
            <div className={styles.RefundDetailsGrid}>
                <div className={styles.RefundCell}>
                    <div className={styles.Label}>Refunded amount</div>
                    <div className={styles.Text}>{money(parseFloat(refund.amount))}</div>
                </div>
                <div className={styles.RefundCell}>
                    <div className={styles.Label}>Net payment</div>
                    <div className={styles.Text}>{money(info.net)}</div>
                </div>
                <div className={styles.RefundCell}>
                    <div className={styles.Label}>Refunded on</div>
                    <div className={styles.Text}>{dayjs(refund.refunded_on).format("MMM D, YYYY")}</div>
                </div>
                <div className={styles.RefundCell}>
                    <div className={styles.Label}>Reason</div>
                    <div className={styles.Text}>{refund.reason_text}</div>
                </div>
                <div className={styles.RefundCell}>
                    <div className={styles.Label}>Recorded by</div>
                    <div className={styles.Text}>{refund.recorded_by || "—"}</div>
                </div>
                <div className={styles.RefundCell}>
                    <div className={styles.Label}>Recorded on</div>
                    <div className={styles.Text}>{dayjs(refund.created_at).format("MMM D, YYYY · h:mm A")}</div>
                </div>
                <div className={`${styles.RefundCell} ${styles.RefundCellFull}`}>
                    <div className={styles.Label}>Memo for renter</div>
                    <div className={styles.Text}>{refund.memo || "—"}</div>
                </div>
            </div>
            {refundCtx && canUpdate(isAuthenticated, user) && <div className={styles.ButtonSec}>
                <button className={`${styles.Button} ${styles.Blue}`} onClick={() => refundCtx.openRefund(item, refund)}>Edit Refund</button>
                <button className={`${styles.Button} ${styles.Red}`} onClick={() => refundCtx.deleteRefund(item, refund)}>Delete Refund</button>
            </div>}
        </div>))}
    </div>);
};

// "Record Refund" for the row's button bar, with the "Up to $X" hint
export const RecordRefundButton = ({ item }) => {
    const { user, isAuthenticated } = useAuth0();
    const refundCtx = useContext(RefundContext);
    const info = getRefundInfo(item);

    if (!refundCtx || !info.eligible || !canUpdate(isAuthenticated, user)) return null;

    const isOnline = item?.payment_type === "online-payment";

    return (<>
        <button className={`${styles.Button} ${styles.RefundButton}`} disabled={info.isFull} onClick={() => refundCtx.openRefund(item, null)}>
            <RotateCcw /> Record Refund
        </button>
        <span className={styles.RefundHint}>
            {info.isFull
                ? (isOnline ? "Fully refunded — nothing left to refund." : "Fully refunded. Delete the refund to edit or delete this payment.")
                : <>Up to <strong>{money(info.remaining)}</strong> · Record only: {isOnline ? "refund the tenant first" : "pay the tenant back yourself first"}, then record it here.</>}
        </span>
    </>);
};
